import React, { createContext, useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';

const API_URL = window.__BACKEND_URL;

export const AppProfileContext = createContext();

/**
 * App Profile Context
 * 
 * PURPOSE: Manages App Profiles that power the Self-Marketing Engine (SME)
 * ACCESS: Admin-only
 * 
 * CORE RULES:
 * - No default App Profile exists
 * - Every SME job must explicitly select an App Profile
 * - App Profiles never affect UI, system emails, or platform visuals
 */
export function AppProfileProvider({ children }) {
  const { user } = useContext(AuthContext);
  const [appProfiles, setAppProfiles] = useState([]);
  const [activeProfileId, setActiveProfileId] = useState(null);
  const [loading, setLoading] = useState(false);

  // Only fetch for admins - strict gating
  const isAdmin = user?.is_internal_admin === true || user?.role === 'admin';

  const refreshProfiles = async () => {
    if (!isAdmin) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/app-profiles`, {
        credentials: 'include'
      });

      let profilesList = [];
      if (response.ok) {
        const data = await response.json();
        // API returns {profiles: [...], storage_mode: ...}
        profilesList = data.profiles || data || [];
      }

      // Filter out any fallback/default profiles - they should not exist
      const realProfiles = Array.isArray(profilesList) 
        ? profilesList.filter(p => p.id !== 'default-app-profile')
        : [];

      const storedActive = localStorage.getItem('active_app_profile_id');
      const activeProfile = realProfiles.find((p) => p.id === storedActive) || realProfiles[0];

      setAppProfiles(realProfiles);
      setActiveProfileId(activeProfile?.id || null);

      if (activeProfile?.id) {
        localStorage.setItem('active_app_profile_id', activeProfile.id);
      }
    } catch {
      // Silently fail - admin endpoint may not exist or user may not have access
      setAppProfiles([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch profiles only for admin users
  useEffect(() => {
    if (isAdmin) {
      refreshProfiles();
    } else {
      // Non-admin: set empty state, no API call
      setLoading(false);
      setAppProfiles([]);
    }
  }, [user, isAdmin]);

  const selectProfile = (profileId) => {
    setActiveProfileId(profileId);
    localStorage.setItem('active_app_profile_id', profileId);
  };

  return (
    <AppProfileContext.Provider
      value={{
        appProfiles,
        activeProfileId,
        loading,
        refreshProfiles,
        selectProfile
      }}
    >
      {children}
    </AppProfileContext.Provider>
  );
}
